import { QueryClient } from "@tanstack/react-query";
import axios, { isAxiosError } from "axios";

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30 * 1000,
			retry: 1,
			refetchOnWindowFocus: false,
		},
	},
});

// Carries a one-shot banner message to /login — sessionStorage (not router
// state) so it survives regardless of which page/flow set it or how the
// redirect happens. Read once (and cleared) by login.page.tsx on mount.
// Used for every case where a toast would race a navigation the user didn't
// explicitly trigger from that same screen: forced logout on 403, manual
// logout, and the forced-logout-after-password-change flow (all three clear
// the session query, which is what makes ProtectedRoute's useSession()
// reactively redirect here — no imperative navigate() call needed for any
// of them).
const AUTH_BANNER_KEY = "authBannerMessage";

export interface AuthBannerPayload {
	message: string;
	type: "info" | "warning" | "success";
}

export function setAuthBanner(payload: AuthBannerPayload): void {
	sessionStorage.setItem(AUTH_BANNER_KEY, JSON.stringify(payload));
}

export function readAndClearAuthBanner(): AuthBannerPayload | null {
	const raw = sessionStorage.getItem(AUTH_BANNER_KEY);
	if (!raw) return null;
	sessionStorage.removeItem(AUTH_BANNER_KEY);
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

// The one cross-cutting case (forced logout + redirect to /login) that
// doesn't belong to any single query/mutation/thunk. Registered on the
// default axios instance — every file in the app imports that same
// singleton — so this fires exactly once per 403, everywhere.
axios.interceptors.response.use(
	(response) => response,
	(error) => {
		if (isAxiosError(error) && error.response?.status === 403) {
			localStorage.clear();
			setAuthBanner({
				message: "Your session has expired. Please sign in again.",
				type: "warning",
			});
			queryClient.setQueryData(["session"], null);
		}
		return Promise.reject(error);
	},
);
